import { useEffect, useRef, useState, useCallback } from "react";
import * as Sentry from "@sentry/browser";
import { diagnosticEventLog } from "../../../shared/services/diagnostic";
import {
  buildPlayerFailureTags,
  getPlayerFailureDiagnostics,
} from "../playerNetworkDiagnostics";

/** Returns true if the error looks like a network/transport failure (vs a 4xx/5xx HTTP or parse error). */
function isNetworkFetchError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message.toLowerCase();
  return (
    err.name === "TypeError" &&
    (msg.includes("fetch") ||
      msg.includes("network") ||
      msg.includes("failed to fetch") ||
      msg.includes("load failed"))
  );
}

// Base URL for squawk history API (will be reverse proxied)
const SQUAWK_HISTORY_BASE_URL = "https://audio.newsquawk.com:6443/api";

// Search terms to try in order of priority
const SEARCH_TERMS_PRIORITY = [
  "powell",
  "trump",
  "nvidia",
  "musk",
  "bessent",
  "greer",
  "openai",
  "nfp",
];

// Target number of audio files to collect
const TARGET_AUDIO_COUNT = 6;

// Maximum number of days to look back for content (handles weekends/holidays)
const MAX_LOOKBACK_DAYS = 5;

interface SquawkHistoryItem {
  id: number;
  filename: string;
  audio: string; // e.g., "/squawks/2025-12-03_23-02-24_channels_multi_asset.webm"
  channels: string[];
  recorded_at: string;
  raw_transcription: string;
  suggested_text: string;
  raw_tagging: Array<{
    name: string;
    category: string;
    confidence: number;
    sentiment: number;
    ticker?: string;
    region?: string;
  }>;
}

interface UseDemoSquawkPlayerOptions {
  audioElement: HTMLAudioElement | null;
  channel: string | null; // demoChannel value (e.g., "energy", "equities")
  enabled: boolean;
}

interface UseDemoSquawkPlayerReturn {
  // Use same status values as WebRTC/MSE players for compatibility
  status: "idle" | "connecting" | "connected" | "error" | "disconnected";
  error: Error | null;
  setVolume: (volume: number) => void;
  stop: () => void;
  retryCount: number;
}

/**
 * Get a date N days ago in YYYY-MM-DD format
 */
const getDateNDaysAgo = (n: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - n);
  return date.toISOString().split("T")[0];
};

/**
 * Probe whether a given day has any recordings for a channel.
 * Uses a cheap channel-only request (no search terms).
 */
const probeForContent = async (channel: string, day: string): Promise<boolean> => {
  const url = `${SQUAWK_HISTORY_BASE_URL}/squawk_history?day=${day}&channel=${channel}`;
  console.log(`[useDemoSquawkPlayer] Probing day ${day} for content`);
  try {
    const response = await fetch(url);
    if (!response.ok) return false;
    const data: SquawkHistoryItem[] = await response.json();
    return data.length > 0;
  } catch {
    return false;
  }
};

/**
 * Find the most recent day with content by probing backwards from yesterday.
 * Returns the day string (YYYY-MM-DD) or null if nothing found within MAX_LOOKBACK_DAYS.
 */
const findRecentDayWithContent = async (channel: string): Promise<string | null> => {
  for (let i = 1; i <= MAX_LOOKBACK_DAYS; i++) {
    const day = getDateNDaysAgo(i);
    const hasContent = await probeForContent(channel, day);
    if (hasContent) {
      console.log(`[useDemoSquawkPlayer] Found content on ${day} (${i} day(s) ago)`);
      return day;
    }
  }
  console.warn(`[useDemoSquawkPlayer] No content found in the last ${MAX_LOOKBACK_DAYS} days`);
  return null;
};

/**
 * Fetch squawk history with multi-pass accumulation strategy:
 * Try each search term in priority order, accumulating results until we have enough
 */
const fetchSquawkHistory = async (
  channel: string,
  day: string
): Promise<SquawkHistoryItem[]> => {
  const collectedItems: SquawkHistoryItem[] = [];
  const seenIds = new Set<number>();

  // Try each search term, accumulating unique results
  for (const term of SEARCH_TERMS_PRIORITY) {
    // Stop if we have enough
    if (collectedItems.length >= TARGET_AUDIO_COUNT) {
      break;
    }

    const url = `${SQUAWK_HISTORY_BASE_URL}/squawk_history?day=${day}&channel=${channel}&text=${term}`;
    console.log(`[useDemoSquawkPlayer] Trying search term "${term}": ${url}`);

    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.warn(`[useDemoSquawkPlayer] HTTP ${response.status} for term "${term}"`);
        continue;
      }
      const data: SquawkHistoryItem[] = await response.json();

      if (data && data.length > 0) {
        // Add unique items we haven't seen yet
        for (const item of data) {
          if (!seenIds.has(item.id) && collectedItems.length < TARGET_AUDIO_COUNT) {
            seenIds.add(item.id);
            collectedItems.push(item);
          }
        }
        console.log(`[useDemoSquawkPlayer] Found ${data.length} results with term "${term}", total collected: ${collectedItems.length}`);
      }
    } catch (err) {
      console.warn(`[useDemoSquawkPlayer] Failed with term "${term}":`, err);
      if (isNetworkFetchError(err)) {
        const errMsg = err instanceof Error ? err.message : String(err);
        const diagnostics = getPlayerFailureDiagnostics(SQUAWK_HISTORY_BASE_URL, "demo", errMsg);
        Sentry.captureException(err, {
          tags: buildPlayerFailureTags(
            "useDemoSquawkPlayer",
            "demo_history_fetch_failed",
            "demo",
            diagnostics
          ),
          extra: {
            term,
            day,
            channel,
            networkDiagnostics: diagnostics.network,
          },
          fingerprint: ["useDemoSquawkPlayer", "history-fetch-failed"],
        });
      }
    }
  }

  // If we still don't have enough, try channel-only as fallback
  if (collectedItems.length < TARGET_AUDIO_COUNT) {
    const urlChannelOnly = `${SQUAWK_HISTORY_BASE_URL}/squawk_history?day=${day}&channel=${channel}`;
    console.log(`[useDemoSquawkPlayer] Fallback to channel-only: ${urlChannelOnly}`);

    try {
      const response = await fetch(urlChannelOnly);
      if (response.ok) {
        const data: SquawkHistoryItem[] = await response.json();
        for (const item of data) {
          if (!seenIds.has(item.id) && collectedItems.length < TARGET_AUDIO_COUNT) {
            seenIds.add(item.id);
            collectedItems.push(item);
          }
        }
        console.log(`[useDemoSquawkPlayer] Channel-only added items, total collected: ${collectedItems.length}`);
      }
    } catch (err) {
      console.warn(`[useDemoSquawkPlayer] Channel-only fallback failed:`, err);
      if (isNetworkFetchError(err)) {
        const errMsg = err instanceof Error ? err.message : String(err);
        const diagnostics = getPlayerFailureDiagnostics(SQUAWK_HISTORY_BASE_URL, "demo", errMsg);
        Sentry.captureException(err, {
          tags: buildPlayerFailureTags(
            "useDemoSquawkPlayer",
            "demo_channel_fallback_fetch_failed",
            "demo",
            diagnostics
          ),
          extra: {
            day,
            channel,
            networkDiagnostics: diagnostics.network,
          },
          fingerprint: ["useDemoSquawkPlayer", "channel-fallback-fetch-failed"],
        });
      }
    }
  }

  console.log(`[useDemoSquawkPlayer] Final collection: ${collectedItems.length} unique audio files`);
  return collectedItems;
};

/**
 * React hook for demo squawk player using historical recordings
 *
 * Fetches 24-hour delayed audio from squawk history API and plays via HTML5 audio
 * Uses same status values as WebRTC/MSE players for UI compatibility
 */
export const useDemoSquawkPlayer = ({
  audioElement,
  channel,
  enabled,
}: UseDemoSquawkPlayerOptions): UseDemoSquawkPlayerReturn => {
  // Use same status values as WebRTC/MSE players
  const [status, setStatus] = useState<"idle" | "connecting" | "connected" | "error" | "disconnected">("idle");
  const [error, setError] = useState<Error | null>(null);

  const audioQueueRef = useRef<string[]>([]);
  const currentIndexRef = useRef<number>(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isPlayingRef = useRef<boolean>(false);
  const currentChannelRef = useRef<string | null>(null);
  const playNextRef = useRef<() => void>(() => {});

  // Set volume helper
  const setVolume = useCallback((volume: number) => {
    if (audioElement) {
      audioElement.volume = Math.max(0, Math.min(1, volume));
    }
  }, [audioElement]);

  // Stop playback
  const stop = useCallback(() => {
    console.log("[useDemoSquawkPlayer] Stopping playback");

    // Abort any pending fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Stop and reset audio element
    if (audioElement) {
      audioElement.pause();
      audioElement.src = "";
      audioElement.load();
    }

    // Reset state
    isPlayingRef.current = false;
    currentChannelRef.current = null;
    audioQueueRef.current = [];
    currentIndexRef.current = 0;
    setStatus("idle");
    setError(null);
  }, [audioElement]);

  // Play next audio in queue
  const playNext = useCallback(() => {
    console.log("[useDemoSquawkPlayer] playNext called", {
      hasAudioElement: !!audioElement,
      queueLength: audioQueueRef.current.length,
      currentIndex: currentIndexRef.current,
      isPlaying: isPlayingRef.current,
    });

    if (!audioElement || audioQueueRef.current.length === 0) {
      // Don't change status here - let the main effect control idle state
      console.log("[useDemoSquawkPlayer] playNext early return - no element or empty queue");
      return;
    }

    const index = currentIndexRef.current;
    if (index >= audioQueueRef.current.length) {
      // Finished playing all audio - stop instead of looping
      console.log("[useDemoSquawkPlayer] Finished playing all audio, stopping");
      diagnosticEventLog.record("demo:audio", "info", "queue-complete", { channel: currentChannelRef.current });
      isPlayingRef.current = false;
      currentChannelRef.current = null;
      audioQueueRef.current = [];
      currentIndexRef.current = 0;
      setStatus("idle");
      return;
    }

    const audioUrl = audioQueueRef.current[index];
    console.log(`[useDemoSquawkPlayer] Playing audio ${index + 1}/${audioQueueRef.current.length}: ${audioUrl}`);

    // Set source and explicitly load before playing
    audioElement.src = audioUrl;
    audioElement.load();

    const playPromise = audioElement.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log("[useDemoSquawkPlayer] Play started successfully");
          diagnosticEventLog.record("demo:audio", "info", "play-started", { index, totalInQueue: audioQueueRef.current.length });
          setStatus("connected"); // "connected" = playing
        })
        .catch((err) => {
          console.error("[useDemoSquawkPlayer] Play error:", err);
          diagnosticEventLog.record("demo:audio", "warn", "play-error", { index, error: err instanceof Error ? err.message : String(err) });
          // Try next audio on error
          currentIndexRef.current++;
          if (currentIndexRef.current < audioQueueRef.current.length) {
            playNextRef.current();
          } else {
            setStatus("error");
            setError(err);
          }
        });
    }
  }, [audioElement]);

  // Keep ref updated with latest playNext
  playNextRef.current = playNext;

  // Handle audio ended - play next in queue
  // Uses ref to always call the latest playNext function
  useEffect(() => {
    if (!audioElement) {
      console.log("[useDemoSquawkPlayer] No audio element for event listeners");
      return;
    }

    console.log("[useDemoSquawkPlayer] Setting up audio event listeners");

    const handleEnded = () => {
      console.log("[useDemoSquawkPlayer] Audio ended event fired", {
        currentIndex: currentIndexRef.current,
        queueLength: audioQueueRef.current.length,
      });
      currentIndexRef.current++;
      // Use setTimeout to ensure state updates from current play have settled
      setTimeout(() => {
        console.log("[useDemoSquawkPlayer] Calling playNextRef.current()");
        playNextRef.current();
      }, 100);
    };

    const handleError = (e: Event) => {
      // Ignore errors when we're not actively playing (e.g., after stop() clears the source)
      if (audioQueueRef.current.length === 0) {
        console.log("[useDemoSquawkPlayer] Ignoring error - queue is empty");
        return;
      }

      console.error("[useDemoSquawkPlayer] Audio element error:", e);
      // Try next audio
      currentIndexRef.current++;
      if (currentIndexRef.current < audioQueueRef.current.length) {
        setTimeout(() => playNextRef.current(), 100);
      } else {
        setStatus("error");
        setError(new Error("Audio playback failed"));
      }
    };

    const handleCanPlay = () => {
      console.log("[useDemoSquawkPlayer] Audio canplay event - ready to play");
    };

    audioElement.addEventListener("ended", handleEnded);
    audioElement.addEventListener("error", handleError);
    audioElement.addEventListener("canplay", handleCanPlay);

    return () => {
      console.log("[useDemoSquawkPlayer] Removing audio event listeners");
      audioElement.removeEventListener("ended", handleEnded);
      audioElement.removeEventListener("error", handleError);
      audioElement.removeEventListener("canplay", handleCanPlay);
    };
  }, [audioElement]); // No longer depends on playNext - uses ref instead

  // Main effect - fetch and start playback when channel changes
  // IMPORTANT: Only depend on values that should trigger a re-fetch
  // Do NOT depend on playNext/stop - they use refs internally
  useEffect(() => {
    console.log("[useDemoSquawkPlayer] Main effect running", { enabled, channel, hasAudioElement: !!audioElement });

    // Don't start if not enabled or missing dependencies
    if (!enabled || !channel || !audioElement) {
      // Only stop if we were previously playing
      if (isPlayingRef.current) {
        console.log("[useDemoSquawkPlayer] Stopping because disabled/no channel/no element");
        // Inline stop logic to avoid dependency issues
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
        audioElement?.pause();
        if (audioElement) {
          audioElement.src = "";
        }
        isPlayingRef.current = false;
        currentChannelRef.current = null;
        audioQueueRef.current = [];
        currentIndexRef.current = 0;
        setStatus("idle");
        setError(null);
      }
      return;
    }

    // Skip if we're already playing the same channel (prevents re-init on dependency changes)
    if (isPlayingRef.current && currentChannelRef.current === channel) {
      console.log("[useDemoSquawkPlayer] Already playing this channel, skipping re-init");
      return;
    }

    // Abort any previous fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const startPlayback = async () => {
      console.log("[useDemoSquawkPlayer] Starting playback for channel:", channel);
      diagnosticEventLog.record("demo:audio", "info", "playback-start", { channel });
      isPlayingRef.current = true;
      currentChannelRef.current = channel;
      setStatus("connecting"); // "connecting" = loading
      setError(null);
      audioQueueRef.current = [];
      currentIndexRef.current = 0;

      try {
        // Probe phase: find the most recent day with content
        const day = await findRecentDayWithContent(channel);

        // Check if we were aborted
        if (abortControllerRef.current?.signal.aborted) {
          console.log("[useDemoSquawkPlayer] Probe was aborted");
          return;
        }

        if (!day) {
          diagnosticEventLog.record("demo:audio", "warn", "no-content", { channel });
          throw new Error(`No recordings available for ${channel} in the last ${MAX_LOOKBACK_DAYS} days`);
        }

        // Fetch phase: run multi-pass search term strategy against the day with content
        const items = await fetchSquawkHistory(channel, day);

        // Check if we were aborted
        if (abortControllerRef.current?.signal.aborted) {
          console.log("[useDemoSquawkPlayer] Fetch was aborted");
          return;
        }

        if (!items || items.length === 0) {
          throw new Error(`No recordings available for ${channel}`);
        }

        // Construct audio URLs from all collected items
        const audioUrls = items.map((item) => `${SQUAWK_HISTORY_BASE_URL}${item.audio}`);

        console.log(`[useDemoSquawkPlayer] Loaded ${audioUrls.length} audio files for ${channel}:`, audioUrls);
        diagnosticEventLog.record("demo:audio", "info", "content-found", { channel, day, audioCount: audioUrls.length });
        audioQueueRef.current = audioUrls;
        currentIndexRef.current = 0;

        // Start playing using the ref to get latest function
        playNextRef.current();
      } catch (err) {
        // Check if we were aborted
        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        console.error("[useDemoSquawkPlayer] Failed to load demo audio:", err);
        const errMsg = err instanceof Error ? err.message : String(err);
        diagnosticEventLog.record("demo:audio", "error", "load-failed", { channel, error: errMsg });
        const diagnostics = getPlayerFailureDiagnostics(SQUAWK_HISTORY_BASE_URL, "demo", errMsg);
        Sentry.captureException(
          err instanceof Error ? err : new Error(errMsg),
          {
            tags: buildPlayerFailureTags(
              "useDemoSquawkPlayer",
              "demo_playback_load_failed",
              "demo",
              diagnostics
            ),
            extra: {
              channel,
              networkDiagnostics: diagnostics.network,
            },
            fingerprint: ["useDemoSquawkPlayer", "playback-load-failed"],
          }
        );
        setStatus("error");
        setError(err instanceof Error ? err : new Error(errMsg));
      }
    };

    startPlayback();

    return () => {
      console.log("[useDemoSquawkPlayer] Main effect cleanup");
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [enabled, channel, audioElement]); // Removed playNext and stop from deps - use refs instead

  return {
    status,
    error,
    setVolume,
    stop,
    retryCount: 0, // Demo player doesn't retry like WebRTC/MSE
  };
};

export default useDemoSquawkPlayer;
